import React from 'react';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { percentageToolTipFormatter,
    generateCompletionRateSeries,
    percentageFormatter } from 'helpers';
import { reduceCreativesData, reduceNameBasedData, shouldShowTotalActivities } from 'helpers/data';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { generateCalloutComponents } from 'helpers/componentGenerators';
import { validateStatisticsData } from 'helpers/validate';
import { toolTipPointFormatterGenerator, labelFormatterGenerator } from 'helpers/charts';
import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchTacticPlacements } from 'services/tactics/duck';
import { fetchChannel, fetchChannelCallouts, fetchChannelCreatives, fetchChannelDynamicCreatives, fetchChannelTargetingTactics, requestChannelUpsell, showedChannelUpsell } from 'services/channel/duck';
import { fetchActivities } from 'services/activities/duck';
import { setObjectiveShadow } from 'services/config/duck';
import { fetchSitesSummaryData } from 'services/ttd/duck';

// Components and Containers
import ObjectiveSwitcher from 'components/molecules/ObjectiveSwitcher';
import LayerThreeDashboardComponent from 'containers/dashboards/DashboardBaseComponent';
import { Grid, Row } from 'react-bootstrap';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import TileWithValidation from 'components/molecules/TileWithValidation';
import RenderingTile from 'components/molecules/RenderingTile';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import ExportPDF from 'containers/buttons/ExportPDF';
import ChannelUpsell from 'components/molecules/ChannelUpsell';
import CreateNewNote from 'containers/buttons/CreateNewNote';
import ViewEntity from 'containers/buttons/ViewEntity';
import Note from 'containers/annotations/NoteContainer';

// Charts
import StackedAreaChart from 'components/molecules/charts/StackedAreaChart';
import ActivitiesStackedAreaChart from 'components/molecules/charts/ActivitiesStackedAreaChart';
import GroupedBarChart from 'components/molecules/charts/GroupedBarChart';
import LineChart from 'components/molecules/charts/LineChart';

// Tables
import ActivitiesTable from 'components/molecules/tables/ActivitiesTable';
import PlacementsTable from 'components/molecules/tables/PlacementsTable';
import ProtectedCreativesReports from 'components/organisms/ProtectedCreativesReports';
import ProtectedTargetingTacticsTable from 'components/organisms/ProtectedTargetingTacticsTable';
import ProtectedDynamicCreativesReports from 'components/organisms/ProtectedDynamicCreativesReports';
import SitesSummaryTable from 'components/molecules/tables/SitesSummaryTable';

const filterEmptyActivitiesColumn = (col, activitiesRenderFlag) =>
    col !== 'ecpa_activities' || (activitiesRenderFlag && col === 'ecpa_activities');

class Dashboard extends LayerThreeDashboardComponent {
    constructor(props) {
        super(props, 'tv');
    }

    makeRequestsForData() {
        const entityId = this.getCurrentEntityId();
        const channel = this.getCurrentChannel();

        const payload = this.createPayload(entityId, channel);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entities.meta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchChannel(payload);
        this.props.fetchChannelCallouts(payload);
        this.props.fetchChannelCreatives(payload);
        this.props.fetchChannelDynamicCreatives(payload);
        this.props.fetchActivities(payload);
        this.props.fetchTacticPlacements(payload);
        this.props.fetchChannelTargetingTactics(payload);
        this.props.fetchSitesSummaryData(payload);
    }

    render() {
        const stub = `l3-${this.getCurrentChannel()}`;
        const {
            backfillBuckets,
            xAxisLabel,
            entityObjective,
            showTotalActivities,
            showTargetingTactics,
            showConversionRevenue,
            selectedTimeRange,
            entityObjectiveLabelCode,
            hideBudgetTimeCharts,
            showTtdSitesSummary
        } = this.props;

        const {
            isLabelCoded,
            eCPAActivitiesOrClicks,
            eCPAActivitiesOrClicksTerm,
            singularTerm,
            helpKeySuffix,
            showActivitiesData,
            calloutTitle,
            primaryActivityField,
            primaryActivityFieldTerm
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);

        const userId = get(this.props, 'currentUser.id', null);
        const entityId = this.getCurrentEntityId();

        const hasCalloutData = get(this.props, 'channel.callouts.params.hasData', false);
        const hasPlacementsData = get(this.props, 'placements.params.hasData', null);
        const hasTargetingTacticsData = get(this.props, 'channel.targetingTactics.params.hasData', null);
        const hasChannelData = get(this.props, 'channel.hasData', false);
        const hasCreativesData = get(this.props, 'channel.creatives.params.hasData', null);
        const hasDynamicCreativesData = get(this.props, 'channel.dynamicCreatives.params.hasData', null);
        const hasTtdSitesSummaryData = get(this.props, 'ttd.sitesSummary.hasData', null);

        const callouts = get(this.props, 'channel.callouts.data', {});
        const data = get(this.props, 'channel.data', {});
        const placements = get(this.props, 'placements', {});
        const targetingTactics = get(this.props, 'channel.targetingTactics', {});
        const creatives = get(this.props, 'channel.creatives', {});
        const calloutWindow = get(this.props, 'channel.callouts.params.timeRanges.previous.windowSize', null);
        const sitesSummaryData = get(this.props, 'ttd.sitesSummary.data', []);

        const activities = get(this.props, 'activities', {});
        const hasActivities = get(this.props, 'activities.hasData', null);
        const activitiesRenderFlag = hasActivities && (showActivitiesData || showTotalActivities);

        let placementsData = null;
        const placementsParams = get(this.props, 'placements.params', {});
        if (hasPlacementsData) {
            placementsData = reduceNameBasedData({
                data: placements,
                namesArrayKey: 'placements',
                injectedKey: 'placementName'
            });
        }

        let targetingTacticsData = {};
        const targetingTacticsParams = get(this.props, 'channel.targetingTactics.params', {});
        if (hasTargetingTacticsData) {
            targetingTacticsData = reduceNameBasedData({
                data: targetingTactics,
                namesArrayKey: 'targetingTactics',
                injectedKey: 'targetingTacticName'
            });
        }


        let creativesData = null;
        const creativesParams = get(this.props, 'channel.creatives.params', {});

        if (hasCreativesData) {
            creativesData = reduceCreativesData(creatives, { filterField: 'started_plays' });
        }

        const isChannelAvailable = get(this.props, 'entities.meta.channelAvailability.tv', false);
        const calloutSettings = [{
            key: 'impressions',
            title: 'Impressions',
            format: '0,0'
        }, { key: 'completed_plays', title: 'Completed Views', format: '0,0' }];
        if (hasActivities) {
            calloutSettings.push({ key: eCPAActivitiesOrClicks, title: calloutTitle, helpKey: `ecpa${helpKeySuffix}`, format: '0,0' });
        }
        calloutSettings.push(showActivitiesData ?
            { key: 'completion_rate', title: 'Completion Rate', format: '0,0.00%' } :
            { key: 'ctr', title: 'CTR', format: '0.00%' }
        );


        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow, isLabelCoded
        });

        const upsellSettings = this.props.entityMeta.upsellSettings;
        const upsellRequested = get(this.props, 'entityMeta.upsellRequested', {});

        const showPlacementsData = hasPlacementsData && activitiesRenderFlag;
        const { orderedColors } = this.props.theme;

        const ChannelActions = <React.Fragment>
            <CreateNewNote />
            <ViewEntity />
            {isChannelAvailable && <ExportPDF template={stub} />}
        </React.Fragment>;

        return isChannelAvailable ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    Advanced TV
                </ChannelTitle>
                {this.props.enableObjectiveSwitching && <ObjectiveSwitcher
                    setObjectiveShadow={this.props.setObjectiveShadow}
                    fetchEntityMeta={this.props.fetchEntityMeta}
                    entityId={this.getCurrentEntityId()}
                    objective={this.props.entityObjective}
                    objectiveLabelCode={this.props.entityObjectiveLabelCode}
                />}
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />

                {/* Call Outs */}
                <Row>
                    {calloutComponents}
                </Row>
                {/* Charts */}
                {hasChannelData ? (
                    <LineChart
                        title="COMPLETION RATE"
                        exportName={`${stub}-completion-rate`}
                        series={generateCompletionRateSeries(data, orderedColors)}
                        data={{ [this.state.channel]: data }}
                        backfillBuckets={backfillBuckets}
                        yTitle="Completion Rate"
                        yOptions={{
                            tooltip: {
                                pointFormatter: percentageToolTipFormatter
                            }
                        }}
                        yAxisOptions={{
                            max: 1, // this prevents the chart from showing over 100%
                            labels: {
                                formatter: percentageFormatter
                            }
                        }}
                        y="completion_rate"
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasChannelData} />
                )}

                {(showPlacementsData) ? (
                    <GroupedBarChart
                        title="PLACEMENT PERFORMANCE"
                        exportName={`${stub}-placement-performance`}
                        data={placementsData.graph}
                        keys={placementsData.sortedKeys}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        y={eCPAActivitiesOrClicks}
                        yAxisLabel={eCPAActivitiesOrClicksTerm}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={showPlacementsData} />
                )}

                { showTtdSitesSummary && (
                    hasTtdSitesSummaryData
                        ? <SitesSummaryTable data={sitesSummaryData} cacheKey={`lq-sites-summary-table-${userId}-${entityId}`}/>
                        : <RenderingTile height='400px' />
                )}

                {(showPlacementsData) ? (
                    <PlacementsTable
                        title={`PLACEMENT ${singularTerm.toUpperCase()} BREAKDOWN`}
                        exportName={`${stub}-placement-activity-breakdown`}
                        data={placementsData.table}
                        params={placementsParams}
                        hasData={hasPlacementsData}
                        showTotalActivities={showTotalActivities}
                        showConversionRevenue={showConversionRevenue}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        accessors={
                            entityObjective === 'ctr' ?
                                ['placement', 'impressions', 'clicks', 'ctr', 'completion_rate', 'spend'] :
                                ['placement', 'impressions', 'ecpa_activities', 'completion_rate', 'spend']
                                    .filter(col => filterEmptyActivitiesColumn(col, activitiesRenderFlag))
                        }
                        defaultSorted={[
                            {
                                id: 'completion_rate',
                                desc: true
                            }
                        ]}
                    />
                ) : (
                    <RenderingTile height='400px' show={showPlacementsData} />
                )}


                <ProtectedTargetingTacticsTable
                    title="TARGETING TACTICS BREAKDOWN"
                    exportName={`${stub}-targeting-tactics-breakdown`}
                    tableType="targetingTactics"
                    data={targetingTacticsData.table}
                    params={targetingTacticsParams}
                    hasData={hasTargetingTacticsData}
                    showTotalActivities={showTotalActivities}
                    showTargetingTactics={showTargetingTactics}
                    showConversionRevenue={showConversionRevenue}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                    accessors={
                        entityObjective === 'ctr' ?
                            ['targetingTactic', 'impressions', 'clicks', 'ctr', 'completion_rate'] :
                            ['targetingTactic', 'impressions', 'ecpa_activities', 'completion_rate']
                                .filter(col => filterEmptyActivitiesColumn(col, activitiesRenderFlag))
                    }
                    defaultSorted={[
                        {
                            id: 'completion_rate',
                            desc: true
                        }
                    ]} />

                {activitiesRenderFlag ? (
                    <ActivitiesStackedAreaChart
                        title={`${singularTerm.toUpperCase()} TRENDS`}
                        exportName={`${stub}-activity-trends`}
                        exportLimit={10}
                        data={activities.data}
                        keys={activities.rankings[primaryActivityField].map(d => d.name).reverse()}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        yAxisLabel={primaryActivityFieldTerm}
                        y={primaryActivityField}
                        colors={this.props.theme.orderedColors}
                        animateDuration={2000}
                        categories={activities.categories} />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                {activitiesRenderFlag ? (
                    <ActivitiesTable
                        title={`${singularTerm.toUpperCase()} BREAKDOWN`}
                        exportName={`${stub}-website-activity-breakdown`}
                        data={activities}
                        showTotalActivities={showTotalActivities}
                        objectives={[entityObjective]}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        filterField={showTotalActivities ? 'total_activities' : primaryActivityField}
                    />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                <ProtectedCreativesReports
                    exportName={`${stub}-creatives`}
                    hasCreativesData={hasCreativesData}
                    creativesData={creativesData}
                    creativesParams={creativesParams}
                    eCPAActivitiesOrClicks={eCPAActivitiesOrClicks}
                    eCPAActivitiesOrClicksTerm={eCPAActivitiesOrClicksTerm}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                    showTotalActivities={showTotalActivities}
                    showConversionRevenue={showConversionRevenue}
                    activitiesOrClicksRenderFlag={activitiesRenderFlag}
                    accessors={['label', 'startedPlays', 'completed_plays', 'completion_rate']}
                />

                <ProtectedDynamicCreativesReports
                    exportName={`${stub}-dynamic-creatives`}
                    hasDynamicCreativesData={hasDynamicCreativesData}
                    dynamicCreatives={get(this.props, 'channel.dynamicCreatives', {})}
                    dynamicProfiles={get(this.props, 'entityMeta.dynamicProfiles', {})}
                    entityObjective={entityObjective}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                />

                <TileWithValidation
                    hasData={hasChannelData && !hideBudgetTimeCharts}
                    innerComponent={StackedAreaChart}
                    innerProps={{
                        title: 'BUDGET REPORT',
                        exportName: `${stub}-budget-report`,
                        data: { [this.state.channel]: data },
                        y: 'spend',
                        xAxisLabel,
                        backfillBuckets,
                        yAxisLabel: 'Budget',
                        yOptions: { formatByType: 'priceWithoutSpace' },
                        toolTipOptions: { pointFormatter: toolTipPointFormatterGenerator },
                        yAxisOptions: {
                            labels: {
                                formatter: labelFormatterGenerator('priceWithoutSpace')
                            }
                        },
                        animateDuration: 2000,
                        xAxisType: 'datetime',
                        validation: {
                            key: 'spend',
                            data: this.props.channel.statistics,
                            validator: validateStatisticsData
                        }
                    }}
                />
            </Grid>
        ) : (
            <Grid fluid={true}>
                <ChannelTitle print={ChannelActions}>
                    Advanced TV
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {upsellSettings.enabled && upsellSettings.tv ? (
                    <ChannelUpsell
                        channel='tv'
                        channelDisplay='Advanced TV'
                        icon='icon-tv-fill'
                        successfulRequest={upsellRequested.tv}
                        buttonClick={() => {
                            const channel = this.getCurrentChannel();
                            return this.props.requestChannelUpsell({ entityId, channel });
                        }}
                        onMount={() => {
                            const channel = this.getCurrentChannel();
                            return this.props.showedChannelUpsell({ entityId, channel });
                        }}
                    />
                ) : (
                    <NoChannelDataTile channel='Advanced TV' />
                )}
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entities: state.entities,
    entityMeta: state.entities.meta,
    objectiveShadow: state.config.objectiveShadow,
    enableObjectiveSwitching: get(state, 'entities.meta.meta.enableObjectiveSwitching', false),
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    showTotalActivities: shouldShowTotalActivities(state),
    showTargetingTactics: get(state, 'entities.meta.meta.showTargetingTactics', false),
    showConversionRevenue: get(state, 'entities.meta.meta.showConversionRevenue', false),
    showTtdSitesSummary: get(state, 'entities.meta.meta.showTtdSitesSummary', false),
    hideBudgetTimeCharts: get(state, 'entities.meta.meta.hideBudgetTimeCharts', false),
    channel: state.channel.tv,
    activities: state.activities.tv,
    placements: state.tactics.placements,
    ttd: state.ttd,
    selectedTimeRange: state.config.selectedTimeRange,
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', [])
});

export default connect(mapStateToProps, {
    fetchEntityMeta,
    fetchChannel,
    fetchChannelCallouts,
    fetchChannelCreatives,
    fetchChannelDynamicCreatives,
    fetchChannelTargetingTactics,
    fetchActivities,
    fetchTacticPlacements,
    fetchSitesSummaryData,
    requestChannelUpsell,
    showedChannelUpsell,
    setObjectiveShadow,
})(Dashboard);



// WEBPACK FOOTER //
// ./src/containers/dashboards/LayerThreeTVContainer.js